import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { FieldError, inputErrorClass } from '../components/FieldError';
import { BackArrowIcon } from '../components/BackArrowIcon';

export function ResetPasswordPage() {
  const location = useLocation();
  const emailFromState = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    setErrors({});
    const e: Record<string, string> = {};
    if (!email) e.submit = 'Start from forgot password to get a code.';
    if (!code.trim()) e.code = 'Code is required.';
    if (!newPassword) e.newPassword = 'New password is required.';
    if (newPassword.length < 6) e.newPassword = 'At least 6 characters.';
    if (newPassword !== confirm) e.confirm = 'Passwords do not match.';
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(email, code.trim(), newPassword);
      setDone(true);
    } catch (err: unknown) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white px-6 py-16 max-w-lg mx-auto text-center">
        <h1 className="text-3xl font-black mb-4">Password reset</h1>
        <p className="text-slate-500 mb-8">You can now sign in with your new password.</p>
        <Link to="/signin/login" className="inline-block py-5 px-12 rounded-2xl bg-blue-600 text-white font-black">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8 max-w-lg mx-auto">
      <Link
        to="/forgot-password"
        className="mb-8 inline-flex items-center gap-1.5 text-sm text-slate-500"
      >
        <BackArrowIcon />
        Back
      </Link>
      <h1 className="text-3xl font-black text-slate-900 mb-2">Set new password</h1>
      <p className="text-slate-500 mb-8">Enter the code from your email.</p>

      {!email && (
        <p className="text-amber-600 text-sm mb-4">
          No email in session.{' '}
          <Link to="/forgot-password" className="underline">
            Request a code first
          </Link>
          .
        </p>
      )}

      <label className="block text-xs font-black text-blue-600 uppercase mb-2">Email</label>
      <input
        className="w-full rounded-2xl bg-slate-100 px-4 py-4 mb-4"
        value={email}
        onChange={(ev) => setEmail(ev.target.value.trim().toLowerCase())}
        readOnly={Boolean(emailFromState)}
        type="email"
      />

      <label className="block text-xs font-black text-blue-600 uppercase mb-2">Code</label>
      <input
        className={`w-full rounded-2xl bg-slate-100 px-4 py-4 mb-1 ${errors.code ? inputErrorClass : ''}`}
        value={code}
        onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
      />
      <FieldError message={errors.code} />

      <label className="block text-xs font-black text-blue-600 uppercase mb-2 mt-4">New password</label>
      <input
        type="password"
        className={`w-full rounded-2xl bg-slate-100 px-4 py-4 mb-1 ${errors.newPassword ? inputErrorClass : ''}`}
        value={newPassword}
        onChange={(ev) => setNewPassword(ev.target.value)}
      />
      <FieldError message={errors.newPassword} />

      <label className="block text-xs font-black text-blue-600 uppercase mb-2 mt-4">Confirm</label>
      <input
        type="password"
        className={`w-full rounded-2xl bg-slate-100 px-4 py-4 mb-1 ${errors.confirm ? inputErrorClass : ''}`}
        value={confirm}
        onChange={(ev) => setConfirm(ev.target.value)}
      />
      <FieldError message={errors.confirm} />

      <FieldError message={errors.submit} />
      <button
        type="button"
        disabled={loading}
        onClick={submit}
        className="w-full py-5 rounded-2xl bg-blue-600 text-white font-black mt-8 disabled:opacity-50"
      >
        {loading ? '…' : 'Reset password'}
      </button>
    </div>
  );
}
